import { useCallback, useId, useRef, useState } from 'react';
import type { CanonicalExam } from '../../core/model/canonical.ts';
import type { ValidationReport } from '../../core/validator/report.ts';
import { useI18n } from '../../i18n/index.tsx';
import { Button, Callout, Spinner } from '../../ui/primitives.tsx';
import { importExamFile } from '../../workers/importClient.ts';
import type { ImportStage } from '../../workers/importProtocol.ts';
import { APP_VERSION } from '../../app/version.ts';

export interface ImportScreenProps {
  readonly onLoaded: (exam: CanonicalExam, report: ValidationReport) => void;
  readonly onRejected: (report: ValidationReport) => void;
}

/**
 * The first screen: choose a file.
 *
 * Both ways in — the picker and drag-and-drop — end in the same call, because the drop zone is an
 * addition to a real `<input type="file">` and never a replacement for it. Dropping a file is not
 * something you can do from a keyboard.
 */
export function ImportScreen({ onLoaded, onRejected }: ImportScreenProps) {
  const { t } = useI18n();
  const inputId = useId();
  const inputRef = useRef<HTMLInputElement>(null);

  const [stage, setStage] = useState<ImportStage | null>(null);
  const [dragging, setDragging] = useState(false);
  const [crash, setCrash] = useState<string | null>(null);

  const handleFile = useCallback(
    async (file: File) => {
      setCrash(null);
      setStage('reading');

      const result = await importExamFile(file, {
        appVersion: APP_VERSION,
        onProgress: setStage,
      });

      setStage(null);

      if (result.ok) {
        onLoaded(result.exam, result.report);
      } else if ('crashed' in result) {
        setCrash(result.message);
      } else {
        onRejected(result.report);
      }
    },
    [onLoaded, onRejected],
  );

  const busy = stage !== null;

  return (
    <section className="screen screen--import" aria-labelledby={`${inputId}-title`}>
      <h1 id={`${inputId}-title`}>{t('import.title')}</h1>
      <p className="lede">{t('import.instructions')}</p>

      <div
        className={`dropzone${dragging ? ' dropzone--active' : ''}`}
        onDragOver={(event) => {
          event.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => {
          setDragging(false);
        }}
        onDrop={(event) => {
          event.preventDefault();
          setDragging(false);
          const file = event.dataTransfer.files[0];
          if (file !== undefined) {
            void handleFile(file);
          }
        }}
      >
        <svg className="dropzone__icon" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="M12 16V4m0 0L8 8m4-4 4 4M4 15v3a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-3"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>

        {/* The input is the control; the drop zone is a convenience layered on top of it. */}
        <input
          ref={inputRef}
          id={inputId}
          type="file"
          accept=".json,application/json"
          className="visually-hidden"
          disabled={busy}
          onChange={(event) => {
            const file = event.target.files?.[0];
            if (file !== undefined) {
              void handleFile(file);
            }
            // Allow re-choosing the same file after fixing it on disk.
            event.target.value = '';
          }}
        />

        <Button
          variant="primary"
          disabled={busy}
          onClick={() => inputRef.current?.click()}
          aria-describedby={`${inputId}-privacy`}
        >
          {t('import.choose')}
        </Button>

        <p className="dropzone__hint" aria-hidden="true">
          {t('import.dropHere')}
        </p>
      </div>

      {busy && <Spinner label={stage === 'reading' ? t('import.reading') : t('import.validating')} />}

      {crash !== null && (
        <Callout tone="error" title={t('report.title')}>
          <p>{crash}</p>
        </Callout>
      )}

      {/*
        The template for making an exam, served by this site.

        A plain anchor with `download` rather than a button: the file is published by the build at a
        stable path, so the browser can fetch and save it with no script involved. The URL goes
        through `BASE_URL` so a deployment under a repository sub-path points at its own copy — and
        so that nobody has to visit a code host to get started.
      */}
      <p className="import__template">
        <a href={`${import.meta.env.BASE_URL}schema/model-schema.json`} download="model-schema.json">
          {t('import.downloadTemplate')}
        </a>{' '}
        <span className="import__template-hint">{t('import.downloadTemplateHint')}</span>
      </p>

      <p className="import__footnote" id={`${inputId}-privacy`}>
        {t('app.privacy')}
      </p>
    </section>
  );
}
